import * as v from "@badrap/valita";
import * as cbor from "cbor-x";
import { base64url } from "rfc4648";

export namespace Token {
  export type Claims<T> = [T] extends [never]
    ? {
        typ: string;
        sub: string;
        iat: number;
        exp: number;
      }
    : {
        typ: string;
        sub: string;
        iat: number;
        exp: number;
        dat: T;
      };

  export namespace subtle {
    export const intoParts = async (
      token: string,
    ): Promise<{
      payload: Uint8Array;
      signature: Uint8Array;
    }> => {
      const [payloadB64, signatureB64] = token.split(".", 2);

      if (payloadB64 === undefined || signatureB64 === undefined) {
        throw new Error(
          "invalid token format, expected dot separated payload & signature",
        );
      }

      const payload = await Promise.try(() =>
        base64url.parse(payloadB64, { loose: true }),
      ).catch((cause) =>
        Promise.reject(
          new Error(
            "invalid token format, expected base64url encoded payload",
            {
              cause,
            },
          ),
        ),
      );

      const signature = await Promise.try(() =>
        base64url.parse(signatureB64, { loose: true }),
      ).catch((cause) =>
        Promise.reject(
          new Error(
            "invalid token format, expected base64url encoded signature",
            { cause },
          ),
        ),
      );

      if (signature.length !== 32) {
        throw new Error("invalid token format, expected 256 bits signature");
      }
      return {
        payload,
        signature,
      };
    };

    export const parsePayload = async (
      payload: Uint8Array,
    ): Promise<Claims<unknown>> => {
      if (payload.length > 1000) {
        throw new Error("token payload too large");
      }

      const claimsParserResult = v
        .object({
          typ: v.string(),
          sub: v.string(),
          iat: v.number(),
          exp: v.number(),
          dat: v.unknown().optional(),
        })
        .try(
          await Promise.try(() => cbor.decode(payload)).catch((cause) => {
            throw new Error("invalid token payload", { cause });
          }),
        );

      if (!claimsParserResult.ok) {
        throw new Error("invalid token claims", {
          cause: claimsParserResult.message,
        });
      }

      return claimsParserResult.value as Claims<unknown>;
    };
  }
}
